import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { getPublicCookieBannerEnabled } from "@/config/public-runtime-config";
import { useConsent } from "@/hooks/use-consent";
import { ALL_GROUPS, CONSENT_GROUP_INFO, getConsentProvider } from "@/lib/consent";
import { getCookieUrl, getPrivacyUrl } from "@/theme/ui-branding";

type ConsentBannerProps = {
  /** Override the privacy-policy link. Defaults to the theme branding's `links.privacy`. */
  privacyPolicyHref?: string;
  cookiePolicyHref?: string;
};

/**
 * First-party cookie-consent banner. Consent is recorded per category, using the
 * same five OneTrust groups and the same wire format, so both providers are read
 * through one interface.
 *
 * Renders nothing where OneTrust is deployed — the CMP owns consent there, and
 * two banners disagreeing about one decision is worse than either alone.
 */
export const ConsentBanner = ({ privacyPolicyHref, cookiePolicyHref }: ConsentBannerProps) => {
  const decided = useConsent(state => state.decided);
  const panelOpen = useConsent(state => state.panelOpen);
  // Switch positions live in the store, which re-seeds them from the saved
  // record on every open — this component stays mounted after a decision, so
  // its own state would go stale.
  const draft = useConsent(state => state.draft);
  const save = useConsent(state => state.save);
  const setDraftGroup = useConsent(state => state.setDraftGroup);
  const openPanel = useConsent(state => state.openPanel);

  const privacyHref = privacyPolicyHref ?? getPrivacyUrl();
  const cookieHref = cookiePolicyHref ?? getCookieUrl();

  if (!getPublicCookieBannerEnabled()) return null;
  if (getConsentProvider() === "onetrust") return null;
  if (decided && !panelOpen) return null;

  return (
    <>
      <div aria-hidden className="fixed inset-0 z-[9998] bg-black/50" />
      <div
        role="dialog"
        aria-live="polite"
        aria-label="Cookie consent"
        aria-modal="true"
        className="fixed inset-x-0 bottom-0 z-[9999] max-h-[85vh] overflow-y-auto border-t border-border bg-card p-4 shadow-lg"
      >
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              We use cookies to keep you signed in, to understand how our site is used, and to measure our campaigns.
              Necessary cookies are always on; everything else is your choice.{" "}
              {privacyHref ? (
                <a
                  href={privacyHref}
                  target="_blank"
                  rel="noreferrer"
                  className="text-info underline-offset-4 hover:underline"
                >
                  Privacy Policy
                </a>
              ) : null}
              {privacyHref && cookieHref ? " and " : null}
              {cookieHref ? (
                <a
                  href={cookieHref}
                  target="_blank"
                  rel="noreferrer"
                  className="text-info underline-offset-4 hover:underline"
                >
                  Cookie Policy
                </a>
              ) : null}
            </p>
            <div className="flex shrink-0 gap-2">
              {panelOpen ? (
                <>
                  {/* Only in the panel: withdrawal stays reachable without
                      turning five switches off by hand. */}
                  <Button variant="outline" size="sm" onClick={() => save([])}>
                    Reject all
                  </Button>
                  <Button variant="default" size="sm" onClick={() => save(draft)}>
                    Save preferences
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" size="sm" onClick={openPanel}>
                    Manage preferences
                  </Button>
                  <Button variant="default" size="sm" onClick={() => save(ALL_GROUPS)}>
                    Accept all
                  </Button>
                </>
              )}
            </div>
          </div>

          {panelOpen ? (
            <ul className="flex flex-col gap-3 border-t border-border pt-3">
              {CONSENT_GROUP_INFO.map(group => {
                const inputId = `consent-group-${group.id}`;
                return (
                  <li key={group.id} className="flex items-start justify-between gap-4">
                    <div className="flex flex-col gap-0.5">
                      <label htmlFor={inputId} className="text-sm font-medium text-foreground">
                        {group.name}
                      </label>
                      <p className="text-xs text-muted-foreground">{group.description}</p>
                    </div>
                    <Switch
                      id={inputId}
                      className="mt-0.5 shrink-0"
                      checked={group.locked || draft.has(group.id)}
                      disabled={group.locked}
                      aria-label={group.locked ? `${group.name} (always active)` : group.name}
                      onCheckedChange={on => setDraftGroup(group.id, on)}
                    />
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      </div>
    </>
  );
};
